/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import logo from '/images/logo.svg';
import LinkColumn from './link-column';
import SocialIcons from './social-icons';
import NewsletterCta from './newsletter-cta';
import LegalBar from './legal-bar';
import type { FullFooterContent } from '@/lib/content/types';

/**
 * SRAM site footer (Figma node `1666:69818`, file `0wdp9xcnSpwcmFwq8arpgy`).
 *
 * Content is sourced entirely from SFCC Content Assets (CRCB2B-48) — nav columns, social links, the
 * newsletter CTA, and the legal bar. Every section degrades independently: when its asset is
 * missing, offline, or schema-invalid, that section simply does not render — there is no fallback
 * copy. See `src/lib/content/footer-content.server.ts` and `docs/README-CONTENT-ASSETS.md`.
 *
 * Below-the-fold content, so the whole content-dependent region is wrapped in exactly one
 * `<Suspense fallback={null}>` around one promise — see design.md Decision 11 and CLAUDE.md's
 * Suspense rules. `footerContent` is created once in `_app.tsx`'s loader and passed down; it is
 * never composed here.
 *
 * LAYOUT AND TOKENS ARE UNCHANGED from the pre-CRCB2B-48 mockup — only the data source moved. See
 * git history for the layout rationale (section-container gutters, `--footer-*` token mapping, the
 * deliberate 24px gap deviation from Figma's legal-band spec, now inside `legal-bar.tsx`).
 *
 * `NewsletterCta`'s `modal` variant is intentionally inert (button renders, no handler) — it is a
 * placeholder for a real newsletter signup modal whose design/integration is still undecided (see
 * design.md). `signup.tsx` and `newsletter-section.tsx` predate CRCB2B-48 (authored by
 * vmarta_sfemu) and are UNRENDERED but intentionally kept on disk, intact, for that future modal —
 * do not delete them, and do not wire them back up without checking the design first.
 */
export default function MainFooter({
    footerContent,
}: {
    footerContent: Promise<FullFooterContent> | undefined;
}): ReactElement | null {
    const { t } = useTranslation('footer');

    if (!footerContent) {
        return null;
    }

    return (
        <footer className="mt-auto">
            {/* SRAM dark footer: scope the semantic text tokens to light-on-dark values within this
                container only, so any nested child that reaches for `text-muted-foreground` /
                `hover:text-foreground` stays readable on the dark surface without being special-cased. */}
            <div className="bg-footer-background [--foreground:var(--base-core-white)] [--muted-foreground:var(--base-core-grey-300)]">
                <Suspense fallback={null}>
                    <Await resolve={footerContent}>
                        {(content) => (
                            <>
                                {/* Figma "Main container" (node `10336:63044`) — 32px padding, 24px stack gap. */}
                                <div className="section-container py-8">
                                    {/* Figma container `1808:19074` — row gap 40px, column gap 72px. */}
                                    <div className="flex flex-wrap items-start gap-x-18 gap-y-10">
                                        {/* Figma "Brand and social links" (node `1808:19719`) — 40px between items. */}
                                        <div className="flex flex-col items-start gap-10 min-w-[220px] max-w-[260px]">
                                            <Link to="/">
                                                <img src={logo} alt={t('logoAlt')} className="h-6 w-auto" />
                                            </Link>

                                            {content.social?.social && <SocialIcons social={content.social.social} />}

                                            {content.social?.cta && <NewsletterCta cta={content.social.cta} />}
                                        </div>

                                        {/* Figma "Slot" (node `1808:19726`) — the link columns wrap at 24px gaps. */}
                                        {content.columns.length > 0 && (
                                            <div className="flex flex-1 flex-wrap items-start gap-6 min-w-[280px] max-w-[960px]">
                                                {content.columns.map((column) => (
                                                    <LinkColumn
                                                        key={column.heading}
                                                        heading={column.heading}
                                                        links={column.links}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <LegalBar legal={content.legal} />
                            </>
                        )}
                    </Await>
                </Suspense>
            </div>
        </footer>
    );
}
